import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import Navbar2 from '@/component/navbar2';
import BrandFooter from '@/components/BrandFooter';
import Stars from '@/components/Stars';
import TeamAvatar from '@/components/TeamAvatar';
import Dh from '@/components/Dh';
import { L7_MEDIA } from '@/data/landingMedia';
import {
  HTReveal, SHead, AudienceGrid, StepList, AppFrame, HumanBand, Stats, ClientStory, Compare, Faq,
  Guarantee, Ck, CtaBand, ONLINE_AUDIENCE, type CmpRow,
} from '@/components/landing/HT';
import { BadgeCheck, CalendarDays } from 'lucide-react';
import { nextVatWindow } from '@/utils/filingDates';

/* ============================================================================
   Conncct Accounting — connectcorporate.com/accounting

   Build of the accounting reference landing ("human touch" design). Shared
   chrome sits outside `.ht` so the page's scoped reset can't reach it.

   Pricing stays live: the three plan cards read `pricing/current.packages`,
   the same admin-edited doc that drives /accounting/pricing. The reference's
   hard-coded plan prices are the fallback only.
   ========================================================================== */

const PILLS = [
  { label: 'Creators', dot: '#F0776B' },
  { label: 'Developers', dot: '#5B8DEF' },
  { label: 'Designers', dot: '#E85C9A' },
  { label: 'Online educators', dot: '#F0A640' },
  { label: 'Consultants', dot: '#7A4FC0' },
  { label: 'E-commerce', dot: '#15824E' },
  { label: 'SaaS & agencies', dot: '#2E6BD6' },
];

const STEPS = [
  { h: 'You invoice & upload expenses', p: 'From your phone or laptop, in seconds — with tax settings pre-configured for international platforms (Stripe, YouTube, Amazon, App Store).' },
  { h: 'Your accountant reviews & guides you', p: 'We answer by chat, check your invoices and make sure you never miss a deduction — a real person, not a ticket queue.' },
  { h: 'You confirm, we file to the FTA', p: 'See your VAT and Corporate Tax calculated, confirm in one click, and we submit them to the FTA on time.' },
];

const FACTS = [
  { b: '5%', h: 'VAT', p: 'Since 2018 — registration required once taxable supplies pass AED 375,000.' },
  { b: '9%', h: 'Corporate Tax', p: 'Since 2023 — on profit above AED 375,000, filed annually to the FTA.' },
  { b: 'Soon', h: 'E-invoicing', p: 'On the way — we keep your books in shape ahead of the mandate.' },
  { b: '0', h: 'Missed deadlines', p: 'A managed filing calendar keeps you penalty-free, every cycle.' },
];

const CMP: CmpRow[] = [
  ['Bookkeeping + VAT + Corporate Tax in one place', 'yes', 'Sometimes', 'no'],
  ['Transparent, published pricing', 'yes', 'Quote only', 'yes'],
  ['FTA deadline tracking & alerts', 'yes', 'Manual', 'no'],
  ['Live dashboard & investor-ready reports', 'yes', 'no', 'no'],
  ['Built for digital & foreign-platform income', 'yes', 'Varies', 'no'],
  ['A named accountant on chat', 'yes', 'Varies', 'no'],
];

const FAQS = [
  { q: 'Do I need to register for Corporate Tax?', a: 'Most UAE businesses must register with the FTA, even below the AED 375,000 threshold where the 9% rate applies. We handle registration and the annual filing for you.' },
  { q: 'When do I register for VAT?', a: 'VAT registration is mandatory once taxable supplies exceed AED 375,000 in 12 months, and voluntary above AED 187,500. We assess where you stand and register you when required.' },
  { q: 'Can you file on my behalf?', a: 'Yes. We prepare and submit your VAT and Corporate Tax returns to the FTA, working with a registered tax agent so your filings are formally represented.' },
  { q: 'How is bookkeeping priced?', a: 'By monthly transaction volume — from AED 1,500/mo for up to 100 transactions, scaling to AED 5,000/mo for 500+. You only pay for the volume you have.' },
  { q: 'What about income from foreign platforms?', a: 'Stripe, YouTube, the App Store, Amazon — we’re set up for cross-border digital income and make sure it’s categorised and reported correctly.' },
  { q: 'Do you do audits?', a: 'We prepare you for audit and coordinate with licensed UAE audit partners. Statutory audits are carried out by Ministry-registered auditors we work alongside.' },
  { q: 'Are you only in the UAE?', a: 'For now, yes — we’re focused on doing UAE compliance exceptionally well. Expansion across MENA and beyond is on the roadmap.' },
];

/* Subscription plans — admin-editable via `pricing/current.packages`
   (the same doc /accounting/pricing reads). */
type Pkg = {
  name: string; tag: string; desc: string; inherit: string; features: string[];
  priceYr: number; priceMo: number | null; discount: string | null; trial: string | null;
  govFees: boolean; popular: boolean;
};

const DFLT_PACKAGES: Pkg[] = [
  { name: 'Pulse', tag: '(for existing businesses)', desc: 'For founders who already have a registered business and want to stay compliant, investor-ready and growth-focused.', inherit: 'Includes:', features: ['Business health & investor-readiness report', 'Legal & compliance check (trade licence, MOA, VAT)', 'Corporate Tax registration & advisory', 'Business templates (NDAs, MOA, resolutions)'], priceYr: 1100, priceMo: null, discount: null, trial: '30-day free trial', govFees: true, popular: false },
  { name: 'Total Compliance', tag: '100% FTA-compliant', desc: 'For entrepreneurs who want to stay 100% compliant with UAE regulations.', inherit: 'Everything in Pulse +', features: ['VAT registration + quarterly VAT filings', 'Accounting dashboard (cash flow, P&L, balance sheet)', 'Corporate Tax registration & annual return (FTA)', 'Up to 100 bookkeeping transactions per month', 'Annual financial statements (CFO-ready)'], priceYr: 14399, priceMo: 1499, discount: '20% OFF', trial: null, govFees: true, popular: false },
  { name: 'Business-in-a-Box™', tag: 'Full compliance + dedicated accountant', desc: 'For entrepreneurs who want full legal, accounting and tax compliance, plus a dedicated accountant.', inherit: 'Everything in Total Compliance +', features: ['Quarterly tax planning & estimates', 'Up to 200 bookkeeping transactions per month', 'Quarterly compliance advisory session', 'Investor-ready reporting (deck financials, data room)', 'Priority support'], priceYr: 22999, priceMo: 2399, discount: '20% OFF', trial: null, govFees: true, popular: true },
];

export default function Accounting() {
  const [packages, setPackages] = useState<Pkg[]>(DFLT_PACKAGES);
  const [billing, setBilling] = useState<'mo' | 'yr'>('yr');
  const vat = nextVatWindow();

  useEffect(() => {
    const ref = doc(db, 'pricing', 'current');
    return onSnapshot(ref, snap => {
      const d = snap.data();
      if (d && Array.isArray(d.packages) && d.packages.length) setPackages(d.packages as Pkg[]);
    }, () => { /* keep defaults on error */ });
  }, []);

  const isLoggedIn = useMemo(() => {
    try { const p = JSON.parse(localStorage.getItem('user') || '{}'); return !!(p?.uid && p?.email); }
    catch { return false; }
  }, []);
  const ctaPath = isLoggedIn ? '/dashboard' : '/accounting/signup';

  return (
    <>
      <Navbar2 logoTo="/" signupTo="/accounting/signup" />

      <div className="ht">
        <HTReveal />

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="hero">
          <div className="wrap hgrid">
            <div className="rv">
              <span className="eyebrow">Bookkeeping · VAT · Corporate Tax — UAE</span>
              <h1>Accounting &amp; tax for founders who earn <span className="g">online.</span></h1>
              <p className="sub">
                Run your books, VAT and UAE Corporate Tax from one place — handled by real accountants who
                know digital income. <b>You do what you do best. We handle the FTA.</b>
              </p>
              <div className="pills">
                {PILLS.map(p => <span className="pill" key={p.label}><span className="d" style={{ background: p.dot }} />{p.label}</span>)}
              </div>
              <div className="hero-cta">
                <Link to={ctaPath} className="btn btn-y">Talk to Conncct <span className="arr">→</span></Link>
                <a href="#pricing" className="btn btn-o">See pricing</a>
              </div>
              <div className="trust">
                <span className="rate"><Stars /> <span><b style={{ color: 'var(--ink)' }}>Built by the team behind Wealthface</b> · 300+ startups supported</span></span>
                <span className="badge"><BadgeCheck size={15} color="#15824E" /> Filed by registered FTA tax agents</span>
              </div>
            </div>

            {/* Product-forward hero: the compliance view itself, not a photo. */}
            <div className="visual rv d1">
              <div className="hdash">
                <div className="hh"><b>Your finance health</b><span className="tagpill g" style={{ margin: 0 }}>Live · FTA-aligned</span></div>
                <div className="score2">
                  <div className="big">72%</div>
                  <div className="lb">Capital-readiness — climbing to investor-ready (80%)</div>
                  <div className="bar2"><i style={{ width: '72%' }} /></div>
                </div>
                <div className="mini" style={{ margin: '0 0 12px' }}>
                  <div><b style={{ color: '#2E6BD6' }}>AED 292K</b><span>Income</span></div>
                  <div><b style={{ color: '#7A4FC0' }}>AED 61K</b><span>Expenses</span></div>
                  <div><b style={{ color: '#15824E' }}>AED 231K</b><span>Profit</span></div>
                </div>
                <div className="hrow">
                  <div className="t">VAT return · last quarter<span>Submitted to the FTA</span></div>
                  <span className="tagpill g" style={{ margin: 0 }}>Done</span>
                </div>
                <div className="hrow">
                  <div className="t">Corporate Tax<span>9% on profit &gt; AED 375k</span></div>
                  <span className="tagpill a" style={{ margin: 0 }}>Due soon</span>
                </div>
                <div className="hrow">
                  <div className="t">Bookkeeping<span>187 transactions reconciled</span></div>
                  <span className="tagpill b" style={{ margin: 0 }}>Synced</span>
                </div>
              </div>

              <div className="fcard c2" style={{ top: 0 }}>
                <div className="t"><CalendarDays size={14} color="var(--amber)" /> Next filing</div>
                <div className="s">{vat.dueLabel}</div>
                <span className="tagpill a">{vat.daysLeft} days left</span>
              </div>
              <div className="fcard c4" style={{ top: 'auto', bottom: -8, left: -16, right: 'auto' }}>
                <TeamAvatar name="Malak" initials="M" className="av" />
                <div><div className="t">Your accountant, Malak</div><span className="dotline">Online — here to help</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ── AUDIENCE ─────────────────────────────────────────────────── */}
        <section className="sec alt">
          <div className="wrap">
            <SHead
              k="For digital professionals"
              h2="If your business is online, we know how it’s taxed."
              lead="Digital income has its own rules — foreign platforms, cross-border clients, marketplaces. We’re set up for exactly that."
            />
            <AudienceGrid items={ONLINE_AUDIENCE} />
          </div>
        </section>

        {/* ── HOW WE WORK ──────────────────────────────────────────────── */}
        <section className="sec" id="how">
          <div className="wrap">
            <SHead
              k="How we work"
              h2="A real online accountant. No emails, no paperwork, no waiting."
              lead="Everything connected on one platform — your invoices, expenses, taxes and your dedicated accountant, in the same place."
            />
            <div className="howwrap">
              <AppFrame nav={['Home', 'Income', 'Expenses', 'Taxes', 'Chat support', 'Deductions']}>
                <div className="drow">
                  <div className="l"><b>Next tax filing</b><span>{vat.windowLabel} · {vat.daysLeft} days left</span></div>
                  <Link to="/book-consultation" className="btn btn-o" style={{ padding: '8px 14px', fontSize: 12 }}>Add to calendar</Link>
                </div>
                <div className="drow">
                  <div className="l"><b>Next taxes forecast</b><span>{vat.quarterLabel}</span></div>
                  <div className="amt a">AED 13,391</div>
                </div>
                <div className="drow">
                  <div className="l"><b>Saved via deductions</b><span>{vat.year} to date</span></div>
                  <div className="amt p">AED 3,320</div>
                </div>
                <div className="dkpi">
                  <div style={{ background: '#E7EEFB' }}><b style={{ color: '#2E6BD6' }}>282K</b><span style={{ color: '#2E6BD6' }}>Income</span></div>
                  <div style={{ background: '#F1E9FB' }}><b style={{ color: '#7A4FC0' }}>119K</b><span style={{ color: '#7A4FC0' }}>Expenses</span></div>
                  <div style={{ background: '#E4F3EA' }}><b style={{ color: '#15824E' }}>163K</b><span style={{ color: '#15824E' }}>Profit</span></div>
                </div>
              </AppFrame>
              <StepList steps={STEPS} />
            </div>
          </div>
        </section>

        {/* ── BUILT FOR THE UAE ────────────────────────────────────────── */}
        <section className="sec alt">
          <div className="wrap">
            <SHead
              k="Built for the UAE"
              h2="Local rules. Local deadlines. Done right."
              lead="UAE compliance moves fast — Corporate Tax landed in 2023, e-invoicing is next. We’re built around the FTA, so you don’t have to learn it."
            />
            <div className="factsband">
              {FACTS.map((f, i) => (
                <div className="fb rv" style={{ transitionDelay: `${i * 60}ms` }} key={f.h}>
                  <b>{f.b}</b><h4>{f.h}</h4><p>{f.p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HUMAN TOUCH ──────────────────────────────────────────────── */}
        <section className="sec">
          <div className="wrap">
            <HumanBand
              title="A real accountant on your side — not a ticket number."
              lead="Every founder gets a named, dedicated accountant who knows your business and answers in minutes. Software does the busywork; a human makes the calls that matter."
              feats={['Dedicated accountant', 'Fast chat replies', 'Proactive deadline alerts', 'Human review on every filing']}
              adviser="Malak"
              role="your accountant"
              avatar={<TeamAvatar name="Malak" initials="M" style={{ width: '100%', height: '100%' }} />}
              chat={[
                { from: 'them', text: 'Hi! I noticed your Stripe payouts jumped this quarter — want me to review your VAT position before the deadline?' },
                { from: 'you', text: 'Yes please' },
                { from: 'them', text: 'Done. You’re covered, and I found AED 1,200 in deductions you’d missed. All set for the filing window.' },
              ]}
            />
          </div>
        </section>

        {/* ── PROOF ────────────────────────────────────────────────────── */}
        <section className="sec">
          <div className="wrap">
            <SHead k="Real results" h2="Founders across the UAE already trust their tax to Conncct." />
            <Stats items={[
              { b: '300+', s: 'Businesses managed by our team' },
              { b: '31', s: 'Service combinations covered' },
              { b: '0', s: 'Missed FTA deadlines' },
              { b: '1', s: 'Dashboard for everything' },
            ]} />
            <ClientStory
              quote="Honestly a lifesaver. I had no idea where to start with tax and was completely lost — until I found Conncct."
              name="Lara B."
              note="Content creator · Dubai"
              initial="L"
              photo={L7_MEDIA.accountingClient}
              caption="Books, VAT and Corporate Tax — one team, one file."
            />
          </div>
        </section>

        {/* ── WHY CONNCCT ──────────────────────────────────────────────── */}
        <section className="sec alt">
          <div className="wrap">
            <SHead k="Why Conncct" h2="One platform beats a pile of vendors." />
            <Compare cols={['Conncct', 'Traditional accountant', 'Doing it yourself']} rows={CMP} />
          </div>
        </section>

        {/* ── PRICING (live: pricing/current.packages) ─────────────────── */}
        <section className="sec" id="pricing">
          <div className="wrap">
            <SHead
              k="Transparent pricing"
              h2="Clear plans. No lock-in. Government fees at cost."
              lead="Start with a bundle or pay only for what you need — bookkeeping scales with your transaction volume."
            />
            <div style={{ textAlign: 'center', marginTop: -22, marginBottom: 30 }}>
              <div className="billtog">
                <button type="button" className={billing === 'mo' ? 'on' : ''} onClick={() => setBilling('mo')}>Monthly</button>
                <button type="button" className={billing === 'yr' ? 'on' : ''} onClick={() => setBilling('yr')}>
                  Annual <span className="sv">save 20%</span>
                </button>
              </div>
            </div>

            <div className="price3">
              {packages.map((p, i) => {
                const annualOnly = p.priceMo == null;
                const monthly = billing === 'mo' && !annualOnly;
                const amount = monthly ? p.priceMo! : p.priceYr;
                const unit = monthly ? '/ mo' : '/ yr';
                const showOff = !monthly && !annualOnly && p.discount;
                return (
                  <div className={`pcard rv${p.popular ? ' pop' : ''}`} style={{ transitionDelay: `${i * 70}ms` }} key={p.name + i}>
                    {p.popular && <span className="poptag">MOST POPULAR</span>}
                    <div className="pt">{p.name}</div>
                    <div className="pit">{p.tag || p.desc}</div>
                    <div className="pp">
                      <span><Dh />{Math.round(amount).toLocaleString('en-US')}</span>
                      <small>{unit}</small>
                      {showOff && <span className="off">{p.discount}</span>}
                    </div>
                    {p.trial && <div className="trial">{p.trial}</div>}
                    {p.govFees && <div className="gov">+ government fees, charged at cost</div>}
                    <div className="inh">{p.inherit}</div>
                    <ul>
                      {p.features.map(f => <li key={f}><span className="ck"><Ck size={12} /></span>{f}</li>)}
                    </ul>
                    <div className="pb">
                      <Link to="/accounting/pricing" className={`btn ${p.popular ? 'btn-y' : 'btn-o'}`}>
                        Choose {p.name.replace('™', '')} <span className="arr">→</span>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="alacarte rv">
              À la carte: bookkeeping from <b>AED 1,500/mo</b> · VAT return <b>AED 1,000/qtr</b> ·
              Corporate Tax filing <b>AED 2,000/yr</b> · company setup from <b>AED 17,850</b>.
              <br />Annual billing saves 20% vs monthly. <Link to="/accounting/pricing">See the full pricing page →</Link>
            </p>

            <Guarantee
              title="Filed right, on time — or we fix it free."
              body="If we make an error on a filing we handle, we correct it at no cost. Compliance is our job, not your stress."
            />
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="sec alt" id="faq">
          <div className="wrap">
            <SHead k="Questions, answered" h2="The tax questions online founders actually ask." />
            <Faq items={FAQS} />
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <CtaBand
          title="Your taxes, sorted. Your online business, under control."
          body="Book a free call and we’ll map exactly what your business needs to register, file and stay FTA-compliant — with a real accountant from day one."
          action={<Link to={ctaPath} className="btn btn-y">Book a free call <span className="arr">→</span></Link>}
        />
      </div>

      <BrandFooter />
    </>
  );
}
